import { useEffect, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const initialErrors = {
  email: '',
  expiryDate: '',
};

const CustomBackdrop = (props) => {
  return (
    <Backdrop
      {...props}
      sx={{
        backdropFilter: 'blur(6px)', // Apply the blur effect
        backgroundColor: 'rgba(255, 255, 255, 0.5)', // Optional: semi-transparent background
      }}
    />
  );
};

export default function AssignPassModal({
  open,
  onClose,
  selectedPass,
  onAssign,
  isAssigning,
  onSearchUser,
  searchingUser,
  selectedUser,
  onResetUser,
}) {
  const [email, setEmail] = useState('');
  const [expiryDate, setExpiryDate] = useState(null);
  const [errors, setErrors] = useState(initialErrors);

  useEffect(() => {
    if (!open) {
      setEmail('');
      setExpiryDate(null);
      setErrors(initialErrors);
      onResetUser();
    }
  }, [open, onResetUser]);

  const handleSearch = async () => {
    const input = email.trim();

    if (!input) {
      setErrors((prev) => ({ ...prev, email: 'Email or phone number is required to search for a user.' }));
      return;
    }

    // Detect if input is email (contains @) or phone
    const isEmail = input.includes('@');
    let normalizedInput = input;

    if (isEmail) {
      normalizedInput = input.toLowerCase();
    } else {
      // Normalize phone number: handle "600" prefix replacement with "60"
      if (input.startsWith('600')) {
        normalizedInput = input.replace(/^600/, '60');
      } else {
        normalizedInput = input;
      }
      // Remove any non-digit characters except + at the start
      normalizedInput = normalizedInput.replace(/[^\d+]/g, '');
      if (normalizedInput.startsWith('+')) {
        normalizedInput = normalizedInput.substring(1);
      }
    }

    setErrors(initialErrors);
    const result = await onSearchUser(normalizedInput);

    if (!result.success) {
      const searchType = isEmail ? 'email' : 'phone number';
      setErrors((prev) => ({ ...prev, email: result.message || `Unable to find user by ${searchType}.` }));
    }
  };

  const handleSet30Days = () => {
    const thirtyDaysFromNow = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    setExpiryDate(thirtyDaysFromNow);
    // Clear any expiry date errors when setting the date
    setErrors((prev) => ({ ...prev, expiryDate: '' }));
  };

  const handleAssign = () => {
    const nextErrors = { ...initialErrors };
    let isValid = true;

    if (!selectedUser?._id) {
      nextErrors.email = 'Search and select a user by email or phone before assigning the pass.';
      isValid = false;
    }

    if (!expiryDate) {
      nextErrors.expiryDate = 'Expiry date is required.';
      isValid = false;
    }

    setErrors(nextErrors);

    if (!isValid) {
      return;
    }

    onAssign({
      userId: selectedUser._id,
      serviceCode: selectedPass?.serviceCode,
      expiryDate: new Date(expiryDate).toISOString(),
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" BackdropComponent={CustomBackdrop}>
      <DialogTitle>Assign Pass</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            Pass
          </Typography>
          <Typography variant="h6">{selectedPass?.serviceName || '—'}</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {selectedPass?.description}
          </Typography>
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            margin="dense"
            label="User Email or Phone"
            fullWidth
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={Boolean(errors.email)}
            helperText={errors.email || 'Search for the rider by email or phone number to retrieve their ID.'}
            placeholder="hello@trekapp.com or 60123456789"
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={searchingUser}
            sx={{ minWidth: { sm: 140 }, mb: { xs: 1, sm: 0 } }}
          >
            {searchingUser ? 'Searching…' : 'Search'}
          </Button>
        </Stack>


        {selectedUser && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="body2">
              {selectedUser.firstName || selectedUser.lastName
                ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim()
                : 'No name provided'}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Phone: {selectedUser.phone}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Email: {selectedUser.email}
            </Typography>
            {selectedUser.verificationStatus && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Status: {selectedUser.verificationStatus}
              </Typography>
            )}
          </Box>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <DateTimePicker
              label="Expiry Date"
              value={expiryDate}
              onChange={(value) => setExpiryDate(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  margin="dense"
                  fullWidth
                  error={Boolean(errors.expiryDate)}
                  helperText={errors.expiryDate}
                />
              )}
            />
            <Button
              variant="outlined"
              onClick={handleSet30Days}
              sx={{ minWidth: { sm: 140 }, mb: { xs: 1, sm: 0 } }}
            >
              Set 30 Days
            </Button>
          </Stack>
        </LocalizationProvider>

        <Alert severity="info" sx={{ mt: 2 }}>
          The pass will be assigned immediately without payment. Please confirm the details before proceeding.
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleAssign} variant="contained" disabled={isAssigning || !selectedUser}>
          {isAssigning ? 'Assigning…' : 'Assign Pass'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
